import React, { useEffect, useState } from "react";
import { FileText, Trash2 } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import {
  fileTypeLabel,
  formatFileSize,
  isImageMedia,
  mediaDisplayName,
  mediaUrl,
  type MediaItem,
} from "../lib/media";

// D1/ISO timestamps — normalize to a Date for display.
export function formatMediaDate(value: string): string {
  if (!value) return "—";
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

export function MediaThumb({ item, assetsBaseUrl, className }: {
  item: MediaItem;
  assetsBaseUrl: string | null;
  className?: string;
}) {
  const src = isImageMedia(item) ? mediaUrl(item, assetsBaseUrl, { thumb: true }) : null;
  if (src) {
    return <img src={src} alt={item.alt || ""} loading="lazy" className={`${className} object-cover`} />;
  }
  return (
    <div className={`${className} flex items-center justify-center text-muted-foreground`}>
      <FileText className="h-5 w-5" />
    </div>
  );
}

/** Grid view: selectable cards; clicking a card opens the edit dialog. */
export function MediaGrid({ items, assetsBaseUrl, selected, onToggleSelect, onOpen }: {
  items: MediaItem[];
  assetsBaseUrl: string | null;
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  onOpen: (item: MediaItem) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {items.map((item) => {
        const isSelected = selected.has(item.id);
        return (
          <div
            key={item.id}
            className={`group relative overflow-hidden rounded-md border bg-card transition-shadow ${
              isSelected ? "ring-2 ring-primary" : "hover:shadow-md"
            }`}
          >
            <button
              type="button"
              onClick={() => onOpen(item)}
              className="block w-full text-left"
              title={mediaDisplayName(item)}
            >
              <MediaThumb item={item} assetsBaseUrl={assetsBaseUrl} className="aspect-square w-full bg-muted" />
              <p
                className={`truncate px-2 py-1.5 text-xs ${
                  item.title ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {mediaDisplayName(item)}
              </p>
            </button>
            <Checkbox
              checked={isSelected}
              onChange={() => onToggleSelect(item.id)}
              aria-label={`Select ${mediaDisplayName(item)}`}
              className={`absolute left-2 top-2 ${
                isSelected ? "" : "opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
              }`}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * One table row with inline title/caption editing: drafts are local and saved
 * on blur (or Enter) when changed.
 */
function MediaTableRow({ item, assetsBaseUrl, isSelected, onToggleSelect, onSaveFields, onDelete }: {
  item: MediaItem;
  assetsBaseUrl: string | null;
  isSelected: boolean;
  onToggleSelect: () => void;
  onSaveFields: (item: MediaItem, fields: { title: string; caption: string }) => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(item.title);
  const [caption, setCaption] = useState(item.caption);

  // Re-sync drafts when the item changes underneath (e.g. edit dialog save).
  useEffect(() => {
    setTitle(item.title);
    setCaption(item.caption);
  }, [item.title, item.caption]);

  const commit = () => {
    if (title.trim() === item.title && caption.trim() === item.caption) return;
    onSaveFields(item, { title: title.trim(), caption: caption.trim() });
  };
  const commitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  };

  return (
    <tr className="border-b last:border-0 hover:bg-muted/50">
      <td className="px-4 py-2">
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelect}
          aria-label={`Select ${mediaDisplayName(item)}`}
        />
      </td>
      <td className="px-2 py-2">
        <MediaThumb item={item} assetsBaseUrl={assetsBaseUrl} className="h-10 w-14 rounded bg-muted" />
      </td>
      <td className="px-2 py-2">
        <Input
          value={title}
          placeholder={item.fileName || item.key.split("/").pop()}
          maxLength={150}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
          onBlur={commit}
          onKeyDown={commitOnEnter}
          className="h-8"
          aria-label="Title"
        />
      </td>
      <td className="px-2 py-2">
        <Input
          value={caption}
          placeholder="Add a caption"
          maxLength={500}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCaption(e.target.value)}
          onBlur={commit}
          onKeyDown={commitOnEnter}
          className="h-8"
          aria-label="Caption"
        />
      </td>
      <td className="px-2 py-2 text-muted-foreground">{fileTypeLabel(item)}</td>
      <td className="px-2 py-2 text-muted-foreground">{formatFileSize(item.size)}</td>
      <td className="px-2 py-2 text-muted-foreground">{formatMediaDate(item.createdAt)}</td>
      <td className="px-2 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-destructive"
          title="Delete file"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

/** Table view with multi-select and inline title/caption editing. */
export function MediaTable({ items, assetsBaseUrl, selected, onToggleSelect, onToggleAll, onSaveFields, onDelete }: {
  items: MediaItem[];
  assetsBaseUrl: string | null;
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onSaveFields: (item: MediaItem, fields: { title: string; caption: string }) => void;
  onDelete: (item: MediaItem) => void;
}) {
  const allSelected = items.length > 0 && items.every((item) => selected.has(item.id));
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
          <th className="w-10 px-4 py-3">
            <Checkbox
              checked={allSelected}
              onChange={onToggleAll}
              aria-label="Select all"
            />
          </th>
          <th className="w-16 px-2 py-3 font-medium">File</th>
          <th className="px-2 py-3 font-medium">Title</th>
          <th className="px-2 py-3 font-medium">Caption</th>
          <th className="w-16 px-2 py-3 font-medium">Type</th>
          <th className="w-20 px-2 py-3 font-medium">Size</th>
          <th className="w-24 px-2 py-3 font-medium">Added</th>
          <th className="w-12 px-2 py-3" />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <MediaTableRow
            key={item.id}
            item={item}
            assetsBaseUrl={assetsBaseUrl}
            isSelected={selected.has(item.id)}
            onToggleSelect={() => onToggleSelect(item.id)}
            onSaveFields={onSaveFields}
            onDelete={() => onDelete(item)}
          />
        ))}
      </tbody>
    </table>
  );
}
